import {
  DepartmentOrWorkAreaId,
  isDepartmentId,
  isDepartmentOrWorkaredaId,
  isWorkAreaId,
} from "../../../../-components/tree/department-or-workarea-id"
import { TreePillsInput } from "../../../../-components/tree/tree-pills-input"
import { useTranslation } from "react-i18next"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"
import { useEffect } from "react"
import {
  ActionIcon,
  Button,
  Checkbox,
  Combobox,
  ComboboxOptionProps,
  Group,
  Select,
  useCombobox,
} from "@mantine/core"
import { IconFilter } from "@tabler/icons-react"
import { useSuspenseQueryPost } from "@/typed-client/hooks/use-suspense-query-post"
import { DutyFocus } from "@/@client/Theasoft_ApiServer_DutyScheduling"

export function DepartmentAndWorkareaTreeSelect({
  focus,
  mode,
  onSelectionChanged,
  onlyDispo,
}: {
  focus: DutyFocus
  mode: "single" | "multiple"
  onSelectionChanged: (selectedItems: DepartmentOrWorkAreaId[]) => void
  // temporary solution for cast/duty scheduling
  onlyDispo: boolean
}) {
  const { data: departmentTree } = useSuspenseQueryPost(
    "/api/WorkArea/GetDepartmentAndWorkAreaTreeWithDutyRosterPermissions",
    {
      whereModule: onlyDispo ? ["theaDispo"] : [],
    }
  )

  const data = departmentTree.tree.filter((v) => v.nodeProps !== null)
  const tree = { ...departmentTree, tree: data }

  const firstValue = data.length > 0 ? data[0].value : null
  const firstId = firstValue && isDepartmentId(firstValue) ? firstValue : null

  const [selected, setSelected] = useUserLocalStorage<DepartmentOrWorkAreaId[]>(
    {
      key: `duty-scheduling/${focus}`,
      defaultValue: firstId ? [firstId] : [],
    }
  )

  const selectedDepartments = data.filter((v) =>
    selected.some((x) => x == v.value)
  )

  useEffect(() => onSelectionChanged(selected), [selected])

  const { t } = useTranslation("app_server")
  return mode === "single" ? (
    <>
      <Select
        placeholder={t("Department")}
        value={selected.length > 0 ? selected[0] : null}
        data={data.map((node) => ({
          label: node.label,
          value: node.value,
        }))}
        onChange={(v) => {
          if (v !== null && isDepartmentId(v)) {
            setSelected([v])
          }
        }}
      />
      <WorkareaMultiSelect
        value={selected.filter(isWorkAreaId)}
        setValue={setSelected}
        data={
          selectedDepartments.length === 0
            ? []
            : selectedDepartments[0].children.map((v) => ({
                label: v.label,
                value: v.value,
              }))
        }
      />
    </>
  ) : (
    <TreePillsInput
      miw={250}
      maxPills={3}
      // tree node not compatible with Record<string,any> (maybe becaus its enum)
      {...(tree as any)}
      initialCheckedState={selected}
      placeholder={t("Department")}
      onCheckedStateChange={(v) => {
        if (v === null || v.length === 0) {
          return
        }
        setSelected(v.filter(isDepartmentOrWorkaredaId))
      }}
    />
  )
}

function WorkareaMultiSelect({
  value,
  data,
  setValue,
}: {
  value: string[]
  data: { label: string; value: string }[]
  setValue: (
    val:
      | DepartmentOrWorkAreaId[]
      | ((prevState: DepartmentOrWorkAreaId[]) => DepartmentOrWorkAreaId[])
  ) => void
}) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  })

  const handleValueSelect = (val: string, _: ComboboxOptionProps) => {
    if (isDepartmentOrWorkaredaId(val)) {
      setValue((current) => {
        return current.includes(val)
          ? current.filter((v) => v !== val)
          : [...current, val]
      })
    }
  }

  const options = data.map((item) => (
    <Combobox.Option
      value={item.value}
      key={item.value}
      active={value.includes(item.value) || value.length == 0}
    >
      <Group gap="sm">
        <Checkbox
          readOnly
          checked={value.length === 0 || value.includes(item.value)}
        />
        <span>{item.label}</span>
      </Group>
    </Combobox.Option>
  ))
  const { t } = useTranslation("app_server")

  return (
    <Combobox store={combobox} onOptionSubmit={handleValueSelect}>
      <Combobox.DropdownTarget>
        <Combobox.EventsTarget>
          <ActionIcon
            onClick={() => combobox.toggleDropdown()}
            variant={value.length === 0 ? "default" : "subtle"}
          >
            <IconFilter size={16} />
          </ActionIcon>
        </Combobox.EventsTarget>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown w={300} miw={300}>
        <Button
          size="xs"
          variant="default"
          onClick={() => setValue((v) => v.filter(isDepartmentId))}
        >
          {t("SelectAll")}
        </Button>

        <Combobox.Options>
          {options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty>{t("NoOptionsFound")}</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
